<script setup>
// assets/project/src/components/Components/CompetitionUploadPriceTable.vue
import { ref, reactive, onMounted, h } from 'vue'
import { UploadOutlined, DownloadOutlined  } from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'

const fileList = ref([])
const uploading = ref(false)
const lastUploadInfo = ref(null)
const formState = reactive({
	file: null
})

const notPaidData = ref(null)           // Данные о неоплативших участниках
const loadingNotPaid = ref(false)       // Индикатор загрузки данных

onMounted(() => {
	fetchUploadInfo()
	fetchNotPaidData()
})

function fetchNotPaidData() {
	loadingNotPaid.value = true
	fetch('/competition/get-not-paid-pis')
		.then(response => response.json())
		.then(data => {
			if (data.success) {
				notPaidData.value = data
			} else {
				console.error('Ошибка при получении данных о неоплативших:', data.error)
			}
		})
		.catch(error => {
			console.error('Ошибка при запросе данных о неоплативших:', error)
		})
		.finally(() => {
			loadingNotPaid.value = false
		})
}

function fetchUploadInfo() {
	fetch('/competition/get-csv-sale-upload-info')
		.then(response => response.json())
		.then(data => {
			if (data.success) {
				lastUploadInfo.value = data.upload_info
			}
		})
		.catch(error => {
			console.error('Ошибка при получении информации о загрузке:', error)
		})
}

function beforeUpload(file) {
	const isCsv = file.type === 'text/csv' || file.name.endsWith('.csv')
	if (!isCsv) {
		message.error('Можно загрузить только CSV файл!')
		return false
	}
	
	fileList.value = [file]
	return false
}

function handleUpload() {
	const formData = new FormData()
	
	if (fileList.value.length === 0) {
		message.error('Пожалуйста, выберите файл для загрузки!')
		return
	}
	
	uploading.value = true
	formData.append('file', fileList.value[0])
	
	fetch('/competition/upload-csv-sale-file', {
		method: 'POST',
		body: formData,
	})
		.then(response => response.json())
		.then(response => {
			fileList.value = []
			uploading.value = false
			if (response.success) {
				message.success('Файл успешно загружен!')
				lastUploadInfo.value = response.upload_info
				
				fetchNotPaidData()
			} else {
				message.error('Ошибка при загрузке файла: ' + response.message)
			}
		})
		.catch(error => {
			uploading.value = false
			message.error('Ошибка при загрузке файла')
			console.error('Ошибка:', error)
		})
}

function handleRemove() {
	fileList.value = []
}

function formatFileSize(bytes) {
	if (bytes < 1024) return bytes + ' байт'
	if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' КБ'
	return (bytes / (1024 * 1024)).toFixed(2) + ' МБ'
}

function downloadFile() {
	const link = document.createElement('a')
	link.href = '/competition/download-csv-sale-file'
	link.download = 'prices.csv'
	document.body.appendChild(link)
	link.click()
	document.body.removeChild(link)
}
</script>

<template>
	<div class="csv-uploader">
		<a-card title="Загрузка истории продаж" style="width: 100%">
			<p>Загрузите CSV файл с историей продаж. Существующий файл будет перезаписан.</p>
			
			<a-upload
				:file-list="fileList"
				:before-upload="beforeUpload"
				@remove="handleRemove"
				:maxCount="1"
			>
				<a-button>
					<upload-outlined/>
					Выбрать файл
				</a-button>
			</a-upload>
			
			<div style="margin-top: 16px; margin-bottom: 16px">
				<a-button
					type="primary"
					:loading="uploading"
					@click="handleUpload"
					:disabled="fileList.length === 0"
				>
					{{ uploading ? 'Загрузка...' : 'Загрузить' }}
				</a-button>
			</div>
			
			<div v-if="lastUploadInfo" class="upload-info">
				<a-alert type="info" show-icon>
					<template #message>
						<strong>Последняя загрузка:</strong> {{ lastUploadInfo.last_upload }}
					</template>
					<template #description>
						<div>Пользователь: {{ lastUploadInfo.uploaded_by }}</div>
						<div>Имя файла: {{ lastUploadInfo.filename }}</div>
						<div>Размер файла: {{ formatFileSize(lastUploadInfo.file_size) }}</div>
						<div style="margin-top: 8px;">
							<a-button
								type="link"
								size="small"
								@click="downloadFile"
								:icon="h(DownloadOutlined)"
							>
								Скачать файл
							</a-button>
						</div>
					</template>
				</a-alert>
			</div>
			
			<div v-if="notPaidData" class="not-paid-info" style="margin-top: 16px;">
				<a-alert
					:type="notPaidData.not_paid_participants > 0 ? 'warning' : 'success'"
					show-icon
				>
					<template #message>
						<strong>Статистика оплаты участников</strong>
					</template>
					<template #description>
						<div>Конкурс: {{ notPaidData.competition_name }}</div>
						<div>Всего участников: {{ notPaidData.total_participants }}</div>
						<div>Оплатили: {{ notPaidData.paid_participants }}</div>
						<div>Не оплатили: {{ notPaidData.not_paid_participants }}</div>
						
						<div v-if="notPaidData.not_paid_participants > 0" style="margin-top: 12px;">
							<a-collapse>
								<a-collapse-panel
									key="1"
									:header="`Список неоплативших (${notPaidData.not_paid_participants})`"
								>
									<a-table
										:data-source="notPaidData.not_paid_list"
										:pagination="false"
										:scroll="{ x: 800 }"
										size="small"
									>
										<a-table-column
											title="ФИО"
											data-index="user_fio"
											key="user_fio"
											width="150"
										/>
										<a-table-column
											title="Email"
											data-index="user_email"
											key="user_email"
											width="200"
										/>
										<a-table-column
											title="Проект"
											data-index="project_name"
											key="project_name"
											width="150"
										/>
										<a-table-column
											title="Категория"
											data-index="category"
											key="category"
											width="120"
										/>
										<a-table-column
											title="Дата подачи"
											data-index="created"
											key="created"
											width="120"
										/>
									</a-table>
								</a-collapse-panel>
							</a-collapse>
						</div>
					</template>
				</a-alert>
			</div>
			
			<!-- New: Индикатор загрузки для данных о неоплативших -->
			<div v-if="loadingNotPaid" style="margin-top: 16px;">
				<a-spin>
					<div style="padding: 20px; text-align: center;">
						Загрузка данных о неоплативших участниках...
					</div>
				</a-spin>
			</div>
		</a-card>
	</div>
</template>

<style scoped>
.csv-uploader {
	margin: 20px 0;
}

.upload-info {
	margin-top: 10px;
}
</style>